import { useEffect } from 'react';

/**
 * Publishes the vertical scrollbar's width as `--scrollbar-width` on <html>.
 *
 * Full-bleed breakouts are normally written `margin-inline: calc(50% - 50vw)`,
 * but `100vw` includes the scrollbar while the centred container is laid out in
 * the narrower client width. The breakout therefore overshoots by half the
 * scrollbar on each side: the element sits a few px off-centre and the document
 * gains a horizontal scrollbar. Publishing the real width lets the calc correct
 * for it.
 *
 * Returns nothing — consumers read the custom property from CSS.
 */
const useScrollbarWidth = (): void => {
  useEffect(() => {
    const update = () => {
      const width = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${Math.max(0, width)}px`);
    };

    update();
    window.addEventListener('resize', update);

    // Content growing or shrinking can add or remove the scrollbar without a
    // resize event, so watch the document too.
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    observer?.observe(document.documentElement);

    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, []);
};

export default useScrollbarWidth;
